import styled from '@emotion/styled';
import Typography from '@material-ui/core/Typography';
import { createElement, FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  DailySummaryPerformanceWithAllFieldsFragment,
  PerformanceReportAdAccount,
  PerformanceReportPeriodPattern,
  PerformanceReportSummaryRequest,
  PeriodicImprovementPerformanceWithAllFieldsFragment,
} from '../../../../generated/graphql';
import { extractActualValue, extractImprovementRate } from '../../../../utility/extractPeriodicImprovementPerformance';
import { SPACING_LARGE, SPACING_UNIT } from '../../../constant/spacing';
import Spinner from '../Spinner';
import DailySummaryPerformanceSection from './DailySummaryPerformanceSection';
import EmptyState from './EmptyState';
import ImprovementRateBarChartList from './ImprovementRateBarChartList';
import ImprovementRateTable from './ImprovementRateTable';

type Props = {
  adAccount: PerformanceReportAdAccount | null;
  period: PerformanceReportPeriodPattern;
  request: PerformanceReportSummaryRequest;
  onRequestChange: (request: PerformanceReportSummaryRequest) => void;
  periodicImprovementPerformance: PeriodicImprovementPerformanceWithAllFieldsFragment | null;
  periodicImprovementPerformanceFetching: boolean;
  dailySummaryPerformance: DailySummaryPerformanceWithAllFieldsFragment[] | null;
  dailySummaryPerformanceFetching: boolean;
  className?: string;
};

const convertTableData = (performance: PeriodicImprovementPerformanceWithAllFieldsFragment) => {
  return performance.creatives.map((creative) => {
    const { all, video, staticImage } = creative.values;

    const item = {
      ...creative,
      actualValue: {
        ...extractActualValue(all),
        target: undefined,
      },
      improvementRate: {
        all: { ...extractImprovementRate(all), target: 'all' },
        video: { ...extractImprovementRate(video), target: 'video' },
        staticImage: { ...extractImprovementRate(staticImage), target: 'static_image' },
      },
    };

    return item;
  });
};

const ImprovementRateSection: FC<Props> = ({
  adAccount,
  period,
  periodicImprovementPerformance,
  periodicImprovementPerformanceFetching,
  dailySummaryPerformance,
  dailySummaryPerformanceFetching,
  className,
}) => {
  const improvementRateTableData = useMemo(
    () => (periodicImprovementPerformance ? convertTableData(periodicImprovementPerformance) : undefined),
    [periodicImprovementPerformance],
  );

  if (periodicImprovementPerformanceFetching) {
    return <Spinner />;
  }

  return (
    <Root className={className}>
      <Wrapper>
        <SubHeading variant="h6" color="textSecondary">
          <FormattedMessage
            id={
              adAccount?.network === 'amazon' && period === 'lifetime'
                ? `performance_report_card.options.period_pattern.${period}_amazon`
                : `performance_report_card.options.period_pattern.${period}`
            }
          />
        </SubHeading>
        {improvementRateTableData && improvementRateTableData.length >= 1 ? (
          <ImprovementRateTable data={improvementRateTableData} />
        ) : (
          <EmptyState />
        )}
      </Wrapper>

      <Wrapper>
        <SubHeading variant="h6" color="textSecondary">
          <FormattedMessage id="performance_report_headline.by_creative" />
        </SubHeading>

        {periodicImprovementPerformance && periodicImprovementPerformance.creatives.length >= 1 ? (
          <ImprovementRateBarChartList periodicImprovementPerformance={periodicImprovementPerformance} />
        ) : (
          <EmptyState />
        )}
      </Wrapper>

      <Wrapper>
        <SubHeading variant="h6" color="textSecondary">
          <FormattedMessage id="performance_report_headline.by_date_time" />
        </SubHeading>

        <DailySummaryPerformanceSection
          loading={dailySummaryPerformanceFetching}
          dailySummaryPerformance={dailySummaryPerformance}
          periodicImprovementPerformance={periodicImprovementPerformance}
        />
      </Wrapper>
    </Root>
  );
};

const Root = styled('div')({
  marginBottom: SPACING_LARGE,
});

const Wrapper = styled('div')({
  marginBottom: SPACING_LARGE,
});

const SubHeading = styled(Typography)({
  marginBottom: SPACING_UNIT,
  fontWeight: 400,
});

export default ImprovementRateSection;
